import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FaRocket, FaLock, FaChartLine, FaGlobe } from 'react-icons/fa';


// Import images
import landImage from '../assets/images/land.png';
import oceanImage from '../assets/images/ocean.png';
import spaceImage from '../assets/images/space.png';
import airImage from '../assets/images/air.png';
import airspaceImage from '../assets/images/airspace.jpeg';
import maritimeImage from '../assets/images/maritime.jpeg';
import logo from '../assets/images/logo.png';

// Define animations first
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const AppContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  position: relative;
  background: black;
  color: #fff;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  opacity: 0;
  animation: ${fadeIn} 0.8s ease-out forwards;
  animation-delay: 1.1s;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 80%;
    max-width: 300px;
  }
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 4rem;
  margin: 2rem 0 3rem;
  width: 100%;
  pointer-events: none;
  opacity: 0;
  animation: ${fadeIn} 0.8s ease-out forwards;
  animation-delay: 0.8s;
  
  @media (max-width: 768px) {
    gap: 2rem;
    flex-direction: column;
    align-items: center;
  }
`;

const Title = styled.h1`
  font-size: 4rem;
  margin-bottom: 1.5rem;
  color: white;
  line-height: 1.2;
  font-weight: bold;
  text-shadow: 0 5px 15px rgba(0, 0, 0, 0.9);
  text-align: center;
  opacity: 0;
  animation: ${fadeIn} 0.8s ease-out forwards;
  animation-delay: 0.5s;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.9),
                 0 0 20px rgba(0, 0, 0, 0.8);
    padding: 0 10px;
  }
`;

const ContentContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  z-index: 2;

  @media (max-width: 768px) {
    min-height: 100vh;
  }

  .navbar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 100;
  }
`;

const MainContent = styled.div`
  position: relative;
  height: 100vh;
  padding: 0;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    position: relative;
    overflow: visible;
  }
`;

const AdditionalContent = styled.div`
  position: relative;
  background: black;
  z-index: 3;
`;

const HeroSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  z-index: 2;
  pointer-events: auto;

  @media (max-width: 768px) {
    position: absolute;
    padding: 0 20px;
    z-index: 2;
    margin: 0 auto;
    top: 50vh;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
  }
`;

const HighlightSpan = styled.span`
  color: white;
  position: relative;
  display: inline;
  text-shadow: 0 0 20px rgb(0, 0, 0);
`;

const Subtitle = styled.p`
  font-size: 1.4rem;
  color: rgb(255, 255, 255);
  margin: 0 auto 2.5rem;
  line-height: 1.6;
  max-width: 800px;
  text-align: center;
  text-shadow: 2px 2px 4px rgb(0, 0, 0),
               0 0 10px rgb(0, 0, 0);
  position: relative;
  z-index: 52;
  padding: 0 20px;

  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 2rem;
    padding: 0 15px;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.9),
                 0 0 20px rgba(0, 0, 0, 0.8);
  }
`;

const StatItem = styled.div`
  text-align: center;
  
  h3 {
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    color: white;
    text-shadow: 0 0 20px rgba(255, 255, 255, 0.4);
  }
  
  p {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.8);
  }
`;

const LearnMoreButton = styled.a`
  padding: 14px 36px;
  border: none;
  border-radius: 30px;
  background: linear-gradient(45deg,rgb(255, 255, 255),rgb(236, 236, 236));
  color: black;
  font-size: 1.2rem;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  font-weight: 500;
  position: relative;
  z-index: 52;
  text-decoration: none;
  display: inline-block;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 50px rgba(0, 0, 0, 0.9);
  }
`;

const SecondaryButton = styled(LearnMoreButton)`
  background: transparent;
  color: white;
  border: 2px solid white;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const SectionContainer = styled.section`
  padding: 6rem 5% 4rem;
  background: #000000;
  position: relative;
  z-index: 10;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  &:nth-child(even) {
    background: #050505;
  }
`;

const SectionHeader = styled.div`
  max-width: 800px;
  margin: 0 auto 3rem;
  text-align: center;
  
  h2 {
    font-size: 2.5rem;
    font-weight: bold;
    margin-bottom: 1rem;
    color: white;
    
    span {
      background: linear-gradient(90deg, #3B82F6, #60A5FA);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }
  
  p {
    font-size: 1.2rem;
    color: rgba(255, 255, 255, 0.8);
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: #111111;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
    border-color: rgba(59, 130, 246, 0.5);
  }
  
  h3 {
    font-size: 1.5rem;
    margin: 1rem 0;
    color: white;
  }
  
  p {
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.6;
  }
`;

const IconWrapper = styled.div`
  font-size: 2.5rem;
  color: #3B82F6;
  margin-bottom: 1rem;
`;

const LogoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 2rem;
  margin: 3rem auto;
  max-width: 1000px;
  align-items: center;
`;

const LogoItem = styled.div`
  background: #111111;
  height: 80px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  filter: grayscale(100%) brightness(1.8);
  transition: filter 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  &:hover {
    filter: grayscale(0) brightness(1);
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
    border-color: rgba(59, 130, 246, 0.3);
  }
  
  img {
    max-width: 70%;
    max-height: 50px;
  }
`;

const CTASection = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: #111111;
  border-radius: 16px;
  max-width: 900px;
  margin: 0 auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  h2 {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
    color: white;
  }
  
  p {
    font-size: 1.2rem;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 2rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const DomainGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  width: 100%;
  height: 100vh;
  position: relative;
  gap: 20px;
  background: black;
  z-index: 1;
  padding: 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    height: auto;
    gap: 0;
    padding-top: 0;
    position: relative;
    z-index: 1;
  }
`;

const DomainSection = styled.div`
  position: relative;
  height: 100%;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.5s ease;
  border-radius: 0;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1;
    transition: background 0.3s ease;
  }

  &:hover {
    transform: scale(1.02);
    z-index: 2;
    
    &:before {
      background: rgba(0, 0, 0, 0.2);
    }
  }

  @media (max-width: 768px) {
    height: 45vh;
    min-height: 250px;
    max-height: 300px;
    
    &:before {
      background: rgba(0, 0, 0, 0.3);
    }

    &:first-child {
      margin-top: 0;
    }
  }
`;

const DomainBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  transition: transform 0.5s ease;

  ${props => props.image === maritimeImage && `
    background-position: left center;
  `}

  ${props => props.image === spaceImage && `
    background-position: right center;
  `}

  ${DomainSection}:hover & {
    transform: scale(1.1);
  }
`;

const DomainContent = styled.div`
  position: absolute;
  z-index: 2;
  padding: 2rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  text-align: center;
  color: white;
  transition: all 0.3s ease;
  padding-bottom: 4rem;
  width: 100%;
  top: 0;
  left: 0;

  @media (max-width: 768px) {
    display: none;
  }

  ${DomainSection}:hover & {
    opacity: 1;
  }

  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
    font-weight: bold;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.9),
                 0 0 20px rgba(0, 0, 0, 0.8);
    transform: translateY(20px);
    transition: transform 0.3s ease;
    opacity: 0;
    
    ${DomainSection}:hover & {
      transform: translateY(0);
      opacity: 1;
    }
  }

  p {
    font-size: 1.1rem;
    max-width: 300px;
    line-height: 1.6;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.9),
                 0 0 20px rgba(0, 0, 0, 0.8);
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.3s ease 0.1s;
    
    ${DomainSection}:hover & {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const LoadingLogo = styled.img`
  width: 200px;
  height: auto;
  margin-bottom: 3rem;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% {
      opacity: 0.6;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0.6;
    }
  }
`;

const LoadingText = styled.div`
  color: white;
  font-size: 2rem;
  margin-bottom: 2rem;
  font-weight: bold;
`;

const LoadingProgress = styled.div`
  width: 200px;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  overflow: hidden;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.progress}%;
    background: white;
    transition: width 0.3s ease;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: black;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: opacity 0.5s ease;
  opacity: ${props => props.$isLoading ? 1 : 0};
  pointer-events: ${props => props.$isLoading ? 'auto' : 'none'};
`;

function Home() {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedImageUrls, setLoadedImageUrls] = useState(new Set());
  const totalImages = 4;

  const domains = [
    {
      title: "Land Operations",
      description: "Advanced geospatial intelligence for ground-based operations and logistics.",
      image: landImage,
    },
    {
      title: "Maritime Operations",
      description: "Advanced maritime dispatch optimization and vessel traffic control for enhanced port operations and maritime safety.",
      image: maritimeImage,
    },
    {
      title: "Aerial Operations",
      description: "Advanced air traffic control automation with intelligent vectoring, dispatch optimization, and comprehensive flight operations management.",
      image: airspaceImage,
    },
    {
      title: "Space Operations",
      description: "Cutting-edge satellite tracking and space domain awareness.",
      image: spaceImage,
    },
  ];

  const descriptions = useMemo(() => ({
    "Airfield": "Enabling Optimized Flight Operations with intelligent command and control systems.",
    "Sea": "Enhancing maritime security and efficiency with autonomous vessel management.",
    "Land": "Coordinating ground forces with real-time situational awareness platforms.",
    "Space": "Managing satellite constellations for secure communications and surveillance."
  }), []);

  const rotatingTextRef = useRef(null);
  const [activeDomain, setActiveDomain] = useState(domains[0].title);
  const [isVisible, setIsVisible] = useState(false);

  const handleDomainChange = useMemo(() => (index) => {
    setActiveDomain(domains[index].title);
  }, [domains]);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Set visibility after initial render
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Preload all images
    domains.forEach(domain => {
      const img = new Image();
      img.src = domain.image;
      img.onload = () => {
        setLoadedImageUrls(prev => {
          if (prev.has(domain.image)) return prev;
          const newSet = new Set(prev);
          newSet.add(domain.image);
          const progress = (newSet.size / totalImages) * 100;
          setLoadingProgress(progress);
          
          if (newSet.size === totalImages) {
            setTimeout(() => {
              setIsLoading(false);
            }, 500);
          }
          
          return newSet;
        });
      };
    });
  }, []);

  return (
    <AppContainer>
      <LoadingOverlay $isLoading={isLoading}>
        <LoadingLogo src={logo} alt="GeoSpatios Logo" />
        <LoadingText>Loading... {Math.round(loadingProgress)}%</LoadingText>
        <LoadingProgress progress={loadingProgress} />
      </LoadingOverlay>
      
      <ContentContainer>
        <Navbar />
        <MainContent>
          <DomainGrid>
            {domains.map((domain, index) => (
              <DomainSection key={index}>
                <DomainBackground image={domain.image} />
                <DomainContent>
                  <h3>{domain.title}</h3>
                  <p>{domain.description}</p>
                </DomainContent>
              </DomainSection>
            ))}
          </DomainGrid>
          
          <HeroSection>
            <Title>
              The Ecosystem for Autonomous{" "}
              <HighlightSpan>
                Operations
              </HighlightSpan>{" "}
            </Title>
            
            <Subtitle>
            Unite high-stakes operations in one command center, <br></br>from data to decisions at machine speed
            </Subtitle>
            
            <ButtonsContainer>
              <LearnMoreButton href="/contact">
                Request Demo
              </LearnMoreButton>
              <SecondaryButton href="/manifesto">
                Learn More
              </SecondaryButton>
            </ButtonsContainer>
          </HeroSection>
        </MainContent>

        <AdditionalContent>
          {/* Features Section */}
          <SectionContainer>
            
          </SectionContainer>
          
          {/* Trusted By Section */}
          <SectionContainer>
            
          </SectionContainer>
          
          {/* Footer */}
          <Footer />
        </AdditionalContent>
      </ContentContainer>
    </AppContainer>
  );
}

export default React.memo(Home); 